import { EventEmitter } from "events";
import { getOrCreateMainLogger } from "../logger/main";
import { download } from "./download";
import { appDataIconsPath } from "./appDataFolder";
import { mainBus } from "./MainBus";
import { permissionsManager } from "./PermissionsManager";
import { updateManager } from "./updateManager";
import { autoUpdaterWrapper } from "./autoUpdaterWrapper";
import MonitorInfo from "./MonitorInfo";
import { windowsIndex } from "./mainStore";
import { clearPreloadCache } from "./preloads";
import { ExternalApplicationManager } from "./ExternalApplicationManager";
import { isDevMode, isDevLaunch, isValidURL, isValidProtocol, isNullPresentInUrl, isWindows } from "../util";
import { Manifest, MainProcessBusEvent, Metric, FEALogLevel } from "../types/types";
import { ProtocolHandler } from "./protocolHandler";
import fs from "fs";
import {
	ClearStorageDataOptions,
	nativeImage,
	Tray,
	shell,
	clipboard,
	App,
	systemPreferences,
	screen,
	Notification,
	globalShortcut,
	ProcessMetric,
	NativeImage,
} from "electron";
import process from "process";
import os from "os";
import path from "path";
import { mkdirp } from "mkdirp";
import parseArgs from "minimist";
import { URL } from "url";
import crypto from "crypto";

const logger = getOrCreateMainLogger();

/**
 * Opens a valid url in the operating system's default browser. For security reasons, invalid URLs and those without http/s protocols
 * are rejected.
 */
export const openUrlWithBrowser = (eventObj: { data: string; respond: Function }, allowedProtocols = []) => {
	const allAllowedProtocols = [...allowedProtocols, "http", "https", "mailto"];
	if (!isValidURL(eventObj.data)) {
		const err = `Failed to open browser - invalid url provided: '${eventObj.data}'`;
		logger.error(err);
		eventObj.respond({ err });
	} else if (!isValidProtocol(eventObj.data, allAllowedProtocols)) {
		const err = `Failed to open browser - only http and https protocols are allowed for security reasons! Rejected '${eventObj.data}'`;
		logger.error(err);
		eventObj.respond({ err });
	} else if (isNullPresentInUrl(eventObj.data)) {
		const err = `Failed to open browser - %00 is in pathname of the URL, which is disallowed for of security reasons! Rejected '${eventObj.data}'`;
		logger.error(err);
		eventObj.respond({ err });
	} else {
		shell
			.openExternal(eventObj.data)
			.then(() => eventObj.respond())
			.catch((reason) => eventObj.respond({ err: reason }));
	}
};

/**
 * Opens the folder containing the passed in file
 * @param filepath
 */
export const showInFolder = (filepath: string) => {
	try {
		shell.showItemInFolder(filepath);
	} catch (err: any) {
		logger.error("showInFolder error: ", err?.message);
	}
};

/**
 * Handle all system events/calls
 */
export class MainSystem extends EventEmitter {
	app: App;

	manifest: Manifest;

	externalApplicationManager: ExternalApplicationManager;

	tray: Tray | null = null;

	mainApplicationStarted: boolean = false;

	restarting: boolean = false;

	startupApplicationHandshakeTimer: ReturnType<typeof setTimeout> | null;

	updateDownloaded: boolean = false;

	trayIcon: NativeImage | null = null;

	applicationIcon: NativeImage | null = null;

	protocolHandler: ProtocolHandler;

	constructor(
		app: App,
		manifest: Manifest,
		externalApplicationManager: ExternalApplicationManager,
		protocolHandler: ProtocolHandler
	) {
		super();
		this.app = app;
		this.manifest = manifest;
		this.externalApplicationManager = externalApplicationManager;
		this.protocolHandler = protocolHandler;
		this.bind();
		logger.log("APPLICATION LIFECYCLE: Creating MainSystem");

		// set timer to notify user if startup app (e.g Finsemble) never completes startup handshake.  Timer is cleared when handshake is done (see startupApplicationHandshake below);
		// a 10 second timeout should be more than enough time since the handshake should be the first action the startup application takes
		this.startupApplicationHandshakeTimer = setTimeout(() => {
			// confirm in manifest that should check for handshake from primary application -- note by default the check is disabled
			// note: have to check manifest inside the timer because it's not available in the constructor
			if (this.manifest.startup_app.requireHandshake) {
				// because the startup app (e.g. finsemble) has been invoked, with more things that can go wrong, so a notification is more appropriate here than a showErrorBox
				const notificationData = {
					data: {
						title: "Electron Adapter",
						message: `The application "${this.manifest.startup_app.url}" never signaled it started successfully. Administrator, please verify manifest.startup_app.url is correct.`,
						wait: true, // wait should keep the notification up until user closes, but this doesn't seem to work
					},
				};
				this.notificationBusHandler(notificationData);
			}
		}, 30000);

		// Pass the restart function to the Update Manager. If the Update Manager detects an update, it will call this function to apply the updates and restart the application.
		updateManager.setOnRestartAndUpdateRequested(this.restartAndUpdateRequested);
	}

	bind() {
		this.exit = this.exit.bind(this);
		this.getVersion = this.getVersion.bind(this);
		this.getFEAInfo = this.getFEAInfo.bind(this);
		this.startupApplicationHandshake = this.startupApplicationHandshake.bind(this);
		this.openUrlWithBrowser = this.openUrlWithBrowser.bind(this);
		this.getHostSpecs = this.getHostSpecs.bind(this);
		this.deleteAssets = this.deleteAssets.bind(this);
		this.getMonitorInfo = this.getMonitorInfo.bind(this);
		this.flushStorage = this.flushStorage.bind(this);
		this.getMousePosition = this.getMousePosition.bind(this);
		this.launchExternalProcess = this.launchExternalProcess.bind(this);
		this.restartApplication = this.restartApplication.bind(this);
		this.getProcessList = this.getProcessList.bind(this);
		this.notificationBusHandler = this.notificationBusHandler.bind(this);
		this.setTrayIcon = this.setTrayIcon.bind(this);
		this.setTrayIconDeprecated = this.setTrayIconDeprecated.bind(this);
		this.removeTrayIcon = this.removeTrayIcon.bind(this);
		this.restartAndUpdateRequested = this.restartAndUpdateRequested.bind(this);
		this.getEnvironmentVariable = this.getEnvironmentVariable.bind(this);
		this.getProtocolString = this.getProtocolString.bind(this);
		this.setMainApplicationStarted = this.setMainApplicationStarted.bind(this);
		this.getUpdateAvailable = this.getUpdateAvailable.bind(this);
		this.getCommandLineArguments = this.getCommandLineArguments.bind(this);
	}

	/**
	 * Handle application exit here
	 */
	exit() {
		this.externalApplicationManager
			.closeAllAppsAndWait()
			.catch((err) => {
				logger.error("Error while closing applications during exit", err);
			})
			.finally(() => {
				logger.log("APPLICATION LIFECYCLE: Exiting application");

				// app.exit (as opposed to app.quit) causes Electron to immediately terminate without any "quitting" events
				this.app.exit();
			});
	}

	/**
	 * Clears the cache
	 *
	 * Example usage: https://github.com/ChartIQ/finsemble/blob/2b8e0895136f6c5c53cc89d71a582bf4eb74a799/src/clients/windowClient.ts#L809
	 *
	 */
	async clearCache(eventObj: MainProcessBusEvent) {
		const { data, sender } = eventObj;
		const options = data || {
			appcache: true, // appcache does not seem be a valid value
			cookies: true,
			localstorage: true,
		};
		logger.log("MainSystem.clearCache - clearCache options", options);
		const storageKeys = Object.keys(options);
		const truthyKeys = storageKeys.filter((key) => options[key] === true);
		// Clearing "indexdb" may put Finsemble into an unstable state, so it is filtered out
		const storages = truthyKeys.map((key) => key.toLowerCase()).filter((key) => key !== "indexdb");

		// session is a webContents
		try {
			logger.log("MainSystem.clearCache - Clearing Electron storage data for storages", { storages });
			await sender.session.clearStorageData({ storages } as ClearStorageDataOptions);
		} catch (error) {
			logger.error("Error clearing storage data", error);
		}
		logger.log("MainSystem.clearCache - Electron Cache cleared.");

		if (options.appcache) {
			try {
				logger.log("MainSystem.clearCache - Clearing Electron storage data for appcache", { storages });
				// Clear the Chromium caches
				await sender.session.clearCache();
				// Also clear the code cache (JS files)
				await sender.session.clearCodeCaches({ urls: [] });
			} catch (error) {
				logger.error("Error clearing cache data", error);
			}
		}

		if (options.preload) {
			clearPreloadCache();
		}
	}

	/**
	 * This currently returns the Finsemble Electron Adapter version
	 * @deprecated use getFEAInfo()
	 */
	getVersion(eventObj: MainProcessBusEvent) {
		eventObj.respond(this.app.getVersion());
	}

	/**
	 * Returns FEA and Electron versions
	 */
	getFEAInfo(eventObj: MainProcessBusEvent) {
		eventObj.respond({
			Electron: process.versions.electron,
			FEA: this.app.getVersion(),
			isDevLaunch: isDevLaunch(),
			isDevMode: isDevMode(),
		});
	}

	/**
	 * Get whether there is an update available to the application.
	 */
	async getUpdateAvailable(eventObj: MainProcessBusEvent) {
		logger.debug("getUpdateAvailable called");
		// Updates can NEVER be available unless the platform is Windows; waiting for updates
		// on non-Windows platforms can create a promise which is never resolved, preventing Finsemble
		// from starting fully (both waitForUpdate MUST be true AND code MUST be running on macOs)
		const updateAvailable = isWindows() && (isDevLaunch() ? false : await updateManager.getUpdateAvailabilityPromise());
		logger.debug("getUpdateAvailable", updateAvailable);
		eventObj.respond(updateAvailable);
	}

	/**
	 * Invoked once by Finsemble's System Manager to indicate the startup application successfully started. This function clears the handshake timer.
	 */
	startupApplicationHandshake() {
		if (this.startupApplicationHandshakeTimer) {
			clearTimeout(this.startupApplicationHandshakeTimer);
			this.startupApplicationHandshakeTimer = null;
		}
	}

	/**
	 * Opens a valid url in the operating system's default browser. For security reasons, invalid URLs and those without http/s protocols
	 * are rejected.
	 */
	openUrlWithBrowser(eventObj: MainProcessBusEvent) {
		openUrlWithBrowser(eventObj, this.manifest["finsemble-electron-adapter"]?.allowedBrowserProtocols);
	}

	/**
	 * Deletes assets (those downloaded via the manifest's `appAssets` field)
	 */
	async deleteAssets(eventObj: MainProcessBusEvent) {
		const { mainWindow } = windowsIndex.byWebContentsId[eventObj.sender.id] ?? {};
		logger.log(`System.deleteAssets from ${mainWindow?.windowName}`);
		const err = this.externalApplicationManager.deleteAssets(eventObj.data);
		eventObj.respond({ err });
	}

	/**
	 * Get the current system specs
	 */
	getHostSpecs(eventObj: MainProcessBusEvent) {
		const specs = {
			// The isAeroGlassEnabled() function exists only when running on windows
			// On windows, if isAeroGlassEnabled does not return true then it's not possible to create transparent windows
			// Note: "Aero Glass" is another name for "DWM Composition"
			aeroGlassEnabled: false,
			arch: os.arch(),
			cpus: os.cpus(),
			env: process.env,
			gpu: {},
			hostname: os.hostname(),
			memory: os.totalmem(),
			name: os.type(),
			networkInterfaces: os.networkInterfaces(),
			osVersion: os.release(),
			screenSaver: false,
		};

		// The systeminformation.graphics() call hangs if WMIC is blocked. There doesn't appear to be a way to check
		// whether WMIC is blocked via code, so we provide a manifest property that disables WMI calls.
		// Deprecated on 20210813, defaulting the manifest entry to true
		const { disableWMICalls = true } = this.manifest;
		if (disableWMICalls) {
			eventObj.respond(specs);
		} else {
			const si = require("systeminformation");
			logger.warn(
				"DEPRECATION: The use of gpu is deprecated due to the planned removal of the systeminformation package and itself will be removed in a future version."
			);
			si.graphics((graphicsInfo: any) => {
				specs.gpu = graphicsInfo;
				eventObj.respond(specs);
			});
		}
	}

	/**
	 * Get all of the monitor information
	 */
	getMonitorInfo(eventObj: MainProcessBusEvent) {
		MonitorInfo.getMonitorInfo((monitorInfo: any) => {
			const response = { monitorInfo };
			eventObj.respond(response);
		});
	}

	/**
	 * Flush local storage. Local storage takes a long time to flush on its own so you can force it.
	 */
	async flushStorage(eventObj: MainProcessBusEvent) {
		const { sender } = eventObj;

		sender.session.flushStorageData();
		logger.log("Successfully flushed storage");
	}

	/**
	 * Get the current mouse position. We add left/top.
	 */
	getMousePosition(eventObj: MainProcessBusEvent) {
		const mousePos = screen.getCursorScreenPoint() as any;
		mousePos.left = mousePos.x;
		mousePos.top = mousePos.y;
		eventObj.respond(mousePos);
	}

	/**
	 * This will launch an external(non electron) application
	 */
	async launchExternalProcess(eventObj: MainProcessBusEvent) {
		const spawnFileParams = eventObj.data;
		logger.log(`MainSystem->launchExternalProcess: ${JSON.stringify(spawnFileParams, null, 2)}`);

		// By default we don't allow launching by a specified a path.
		// If a firm wants to allow paths in calls to launchExternalProcess then it must explicitly add this to the manifest config.
		const { removePathInSpawnExternalApps = true } = this.manifest;

		// allowInternalSpawnByPath allows us to invoke this method internally without requiring the user to globally allow spawning by path. Currently we only use this to spawn selfservice.exe for Citrix windows.
		if (removePathInSpawnExternalApps && spawnFileParams?.path && !spawnFileParams?.allowInternalSpawnByPath) {
			const err = {
				status: "error",
				code: "unsupported_argument",
				message:
					"Spawning by path is disabled. To enable spawning by path, set 'removePathInSpawnExternalApps' to false in the manifest.",
			};
			logger.warn(err.message);
			eventObj.respond(err);
			return;
		}

		const { err } = await this.externalApplicationManager.spawn(spawnFileParams);
		if (err) {
			logger.error(`Failed to launch external process ${err}`);
			eventObj.respond({
				status: "error",
				error: err,
			});
			return;
		}
		eventObj.respond({ status: "success" });
	}

	/**
	 * Restart the application and apply the updates.
	 * @param {*} quitAndRestart The function from (auto updater)[https://github.com/electron/electron/blob/54ef9068327d7ac34af06ec133b4cb4ea7edbc8f/lib/browser/api/auto-updater/auto-updater-win.js]
	 * to restart the app and install the update after it has been downloaded.
	 */
	restartAndUpdateRequested() {
		logger.log("APPLICATION LIFECYCLE: Requesting a restart of the application to apply the new updates.");
		this.updateDownloaded = true;

		// Request restart to apply updates
		mainBus.sendEvent("systemEvent.apply-update-requested");
	}

	/**
	 * Restart application
	 * This needs to be updated. There was an issue with parent applications so that is why it is not on the application level. It should be though.
	 */
	async restartApplication() {
		logger.log("APPLICATION LIFECYCLE: Restarting the application");
		if (this.restarting) {
			logger.warn(
				"Restart invoked while the application is restarting. This should not happen. Dropping the request. Check your code for instances of `fin.desktop.System.restart()`"
			);
			return;
		}
		this.restarting = true;

		/**
		 * When running in developer mode (i.e. launched from e2oLauncher) we will exit with code 47 when restarting.
		 * The e2oLauncher will pick this code up and know that FEA is restarting rather than closing, thus allowing the dev background processes to continue.
		 * Why exit code 47? No good reason. The exit code must be less than 255 and only a few of them have standardized meanings.
		 *
		 * (Note that codes greater than 255 are illegal. We used to use code 3375, and it worked in some circumstances but not all.)
		 */
		const exitCode = process.env.ELECTRON_DEV ? 47 : 0;

		this.externalApplicationManager
			.closeAllAppsAndWait()
			.catch((err) => {
				logger.error("Error while closing applications during restart", err);
			})
			.finally(() => {
				if (exitCode === 0) {
					if (this.updateDownloaded) {
						logger.log("APPLICATION LIFECYCLE: Applying updates");
						autoUpdaterWrapper.quitAndInstall();
					} else {
						this.app.relaunch({
							args: process.argv.slice(1).concat(["--relaunch"]),
						});
					}
				}

				this.app.exit(exitCode);
			});
	}

	/**
	 * Collects process information for use by ProcessMonitor.
	 *
	 * Cross-domain sites that use BrowserView will have views in their own process. This appears to be done for security reasons by Electron\Chromium
	 * and is not something we can control. In order to collect information for views that have their own process we need to check each window
	 * and save a list of views and applications that have their own process. We cannot rely on the list of applications for this
	 * as some windows with affinity may end as part of another process but contain a view that has it's own process.
	 *
	 * This electron api does not rely on wmic commands like pidusage so it should not have similar performance problems. Our previous implementation
	 * relied on the process api commands from the render process. Since BrowserViews can only be accessed from the main process the process api is no longer sufficient.
	 */
	async getProcessList(eventObj: { respond: Function }) {
		// (1) Collect metrics from Electron. There will be one array element per operating system process
		const metrics = this.app.getAppMetrics();

		const metricsByProcessId: Record<number, Metric> = {};

		const deriveNameFromType = (metric: ProcessMetric): string => {
			let name = `Renderer-${metric.pid}`;
			switch (metric.type) {
				case "Browser":
					name = "Main Process";
					break;
				case "Tab":
					break;
				default:
					name = metric.type;
					break;
			}

			return metric.name || name;
		};

		// (2) Organize and format metrics for display
		metrics.forEach((metric) => {
			metricsByProcessId[metric.pid] = {
				cpuUsage: metric.cpu.percentCPUUsage,
				name: deriveNameFromType(metric),
				type: metric.type,
				peakWorkingSetSize: metric.memory.peakWorkingSetSize * 1024,
				processId: metric.pid,
				workingSetSize: metric.memory.workingSetSize * 1024,
				webContents: {},
			};
		});

		// (3) Associate the pids with our windowNames and webContentNames for displaying in the Process Monitor
		Object.values(windowsIndex.byWebContentsId).forEach((record) => {
			// Resolve UnhandledPromiseRejectionWarning: TypeError: Object has been destroyed
			if (record.webContents.isDestroyed()) {
				return;
			}

			const pid = record.webContents.getOSProcessId();
			let metric = metricsByProcessId[pid];
			if (!metric) return;

			// There might be a better way to detect affinity windows within the webcontents records,
			// however this might be sufficient since it's only for display in the Process Monitor
			if (record.mainWindow.isAffinitySurrogate) {
				metric.name = `${record.mainWindow.windowName}`;
				return;
			}

			if (record.mainWindow.affinity && !record.browserView && record.webContents?.getURL()) {
				try {
					const url = new URL(record.webContents.getURL());
					const domainParts = url.hostname.split(".").reverse();
					const domain = domainParts.length > 1 ? [domainParts[1], domainParts[0]].join(".") : domainParts[0];

					metric.name = `${record.mainWindow.affinity} (${domain})`;
				} catch (e: any) {
					logger.error("Process monitor error", e.toString(), record.webContents.getURL());
					metric.name = "Unknown URL";
				}
			}

			metric.webContents[record.webContents.id] = {
				windowName: record.mainWindow.windowName,
				webContentsName: record.webContentsName,
				type: record.browserView ? "view" : "window",
			};
		});

		const processList = Object.values(metricsByProcessId);
		eventObj.respond(processList);
	}

	/**
	 * Pops up an electron.Notification. This accepts all parameters accepted by electron.Notification.
	 * The icon will be set to Finsemble's default icon if not provided as a parameter.
	 *
	 * @param {electron.Notification options} notification The notification to send. This should use the same parameters as electron.Notification accepts.
	 * @param {} [options] Additional options for the toast
	 * @param {boolean} [options.devModeOnly] If true then the toast will only occur when in developer mode (e.g. when the ELECTRON_DEV environment variable is set such as when running from the seed).
	 */
	toast(notification: any, { devModeOnly = false } = {}) {
		if (devModeOnly && !isDevMode()) {
			logger.log(`devModeOnly notification squashed: ${JSON.stringify(notification)}`);
			return;
		}
		const notificationParams = {
			...notification,
			icon: notification.icon || this.applicationIcon || this.trayIcon,
		};
		logger.log(`Sending notification: ${JSON.stringify(notificationParams)}`);
		new Notification(notificationParams).show();
	}

	/**
	 * Receives notification toast requests from the render process.
	 */
	notificationBusHandler(eventObj: {
		data: {
			title: string;
			message?: any;
			wait: boolean;
			body?: any;
			content?: any;
			devModeOnly?: boolean;
		};
	}) {
		// Get the notification data out of the eventObj, rationalizing any fields for backward compatibility,
		// and maintaining any unlisted fields so that they can be passed to electron.Notification.
		let notification = {
			...eventObj.data,
			title:
				eventObj.data.message.title ||
				eventObj.data.title ||
				this.manifest.startup_app?.name ||
				this.app.name ||
				"Finsemble",
			body:
				eventObj.data.message.description ||
				eventObj.data.message ||
				eventObj.data.body ||
				eventObj.data.content ||
				"No notification content?",
		};

		// Until we refactor toasts, the Finsemble options will be jammed into the params
		// We should refactor fin object to have two arguments: (1) electron notification options, (2) devModeOnly or other options
		const options = {
			devModeOnly: eventObj.data.devModeOnly,
		};
		this.toast(notification, options);
	}

	/**
	 * Downloads the application icon specified in the manifest. This will be used for toasts.
	 */
	async downloadApplicationIcon() {
		const url = this.manifest.startup_app?.applicationIcon;
		try {
			const { data } = await download(url, { logger });
			this.applicationIcon = nativeImage.createFromBuffer(data);
		} catch (err) {}
	}

	/**
	 * Logs messages to the FEA log. Messages is assumed to be an array from a varargs statement
	 * such as logToDisk("blah", "foo", "wa")
	 */
	logToDisk(eventObj: MainProcessBusEvent) {
		const { level, messages }: { level: FEALogLevel; messages: any[] } = eventObj.data;
		if (!logger[level]) {
			const err = `logToDisk() called with illegal level ${level}`;
			logger.error(err);
			eventObj.respond({ err });
			return;
		}
		logger[level](...messages);
		eventObj.respond({});
	}

	/**
	 * Sets the tray icon and begins processing the events that feed "system-tray-click", etc
	 */
	async setTrayIcon(eventObj: MainProcessBusEvent) {
		const { url } = eventObj.data;
		let data: Buffer | null = null;
		try {
			({ data } = await download(url, { logger }));
		} catch (err: any) {
			logger.warn(`Failed to download icon from ${eventObj.data.iconpath}: ${err.message}`);
		}
		if (!data) return;

		// Icon is blank even though the file exists
		this.trayIcon = nativeImage.createFromBuffer(data);
		this.trayIcon = this.trayIcon.resize({ width: 16, height: 16 }); // this was supposed to be the solution to a blank icon but it doesn't work.
		if (this.tray) {
			// Remove the existing Tray if it exists
			this.tray.destroy();
		}
		const tray = new Tray(this.trayIcon);
		this.tray = tray;
		this.tray.setToolTip(this.manifest.startup_app?.name);

		tray.addListener("click", (event, position: any) => {
			logger.debug("User left-clicked the system tray icon");
			position.event = "click";
			position.left = position.x;
			position.right = position.y;
			position.bounds = { ...position };
			position.button = 0;
			// If the end user is holding down any modifier key while clicking then assume the user is trying
			// to right-click. This happens on trackpads where users are used to holding ctrl or alt to right-click.
			const tryingToRightClick = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
			const finsembleEvent = tryingToRightClick
				? "systemEvent.system-tray-right-click"
				: "systemEvent.system-tray-click";
			mainBus.sendEvent(finsembleEvent, position);
		});

		tray.addListener("right-click", (event, position: any) => {
			logger.debug("User right-clicked the system tray icon");
			position.event = "click";
			position.left = position.x;
			position.right = position.y;
			position.bounds = { ...position };
			position.button = 2;
			mainBus.sendEvent("systemEvent.system-tray-right-click", position);
		});

		tray.addListener("mouse-enter", (event, position) => {
			mainBus.sendEvent("systemEvent.system-tray-mouse-enter", position);
		});
		eventObj.respond();
	}

	/**
	 * @deprecated version of setTrayIcon that had a complex listener scheme
	 * This is called by the renderer's Window.setTrayIcon() which is deprecated in favor
	 * of System.setTrayIcon().
	 */
	async setTrayIconDeprecated(eventObj: MainProcessBusEvent) {
		const currentFolderLocation = appDataIconsPath();
		if (!fs.existsSync(currentFolderLocation)) {
			logger.debug("Creating icon folder", currentFolderLocation);
			mkdirp.sync(currentFolderLocation);
		}
		// MD5 for uniqueness
		const hash = crypto.createHash("md5").update(Buffer.from(eventObj.data.iconpath).toString()).digest("hex");
		const filePath = path.join(currentFolderLocation, hash);
		try {
			const { data } = await download(eventObj.data.iconpath, { logger });
			fs.writeFileSync(filePath, data);
		} catch (err: any) {
			logger.warn(`Failed to download icon from ${eventObj.data.iconpath}: ${err.message}`);
		}

		// Icon is blank even though the file exists
		this.trayIcon = nativeImage.createFromPath(filePath);
		this.trayIcon = this.trayIcon.resize({ width: 16, height: 16 }); // this was supposed to be the solution to a blank icon but it doesn't work.
		const trayIcon = new Tray(this.trayIcon);
		if (!this.tray) {
			this.tray = trayIcon;
		}

		if (eventObj.data.listeners) {
			if (eventObj.data.listeners.includes("clickListener")) {
				trayIcon.addListener("click", (event, position: any) => {
					logger.debug("User left-clicked the system tray icon");
					position.event = "click";
					position.left = position.x;
					position.right = position.y;
					position.bounds = { ...position };
					position.button = 0;
					eventObj.respond(position);
				});

				trayIcon.addListener("right-click", (event, position: any) => {
					logger.debug("User right-clicked the system tray icon");
					position.event = "click";
					position.left = position.x;
					position.right = position.y;
					position.bounds = { ...position };
					position.button = 2;
					eventObj.respond(position);
				});
			}

			// looks like this is mac only
			if (eventObj.data.listeners.includes("hoverListener")) {
				trayIcon.addListener("mouse-enter", (event, position) => {
					eventObj.respond(position);
				});
			}
		}
	}

	/**
	 * @deprecated
	 */
	removeTrayIcon() {
		if (this.tray) {
			this.tray.destroy();
			this.tray = null;
			logger.log("Tray icon deleted.");
		}
	}

	/**
	 * Responds to getEnvironmentVariable request from the render process
	 * @param {*} eventObj -- needs to have a "data.variableName" property, which is either a string or an array of strings
	 */
	getEnvironmentVariable(eventObj: MainProcessBusEvent) {
		const { variableName } = eventObj.data;
		const argumentIsValid = typeof variableName == "string" || Array.isArray(variableName);
		const environmentVariablesExist = Boolean(process.env);

		if (argumentIsValid && environmentVariablesExist) {
			let value;
			if (Array.isArray(variableName)) {
				value = {};
				variableName.forEach((name) => (value[name] = process.env[name] || null));
			} else {
				value = process.env[variableName] || null;
			}
			eventObj.respond({ status: "success", value });
		}
		// it is not an error if any given variable does not exists, but it is an error if:
		// 1. we cannot access environment variables
		// 2. eventObj.data.variableName is not a string or an array
		else {
			eventObj.respond({
				status: "error",
				message: "Error processing environment variable message",
			});
		}
	}

	availableFormatsFromClipboard(eventObj: MainProcessBusEvent) {
		try {
			const formats = clipboard.availableFormats();
			eventObj.respond({ status: "success", value: formats });
		} catch (clipboardReadError) {
			eventObj.respond({
				status: "error",
				message: `Error reading from system clipboard - ${clipboardReadError}`,
			});
		}
	}

	/**
	 * Responds to a request to write data to clipboard from System
	 */
	writeToClipboard(eventObj: MainProcessBusEvent) {
		const { data } = eventObj;

		try {
			clipboard.write(data);
			eventObj.respond({
				status: "success",
				message: "Data copied successfully",
			});
		} catch (clipboardWriteError) {
			eventObj.respond({
				status: "error",
				message: `Error writing to system clipboard - ${clipboardWriteError}`,
			});
		}
	}

	/**
	 * Responds to a request to write text to clipboard from System
	 */
	writeTextToClipboard(eventObj: MainProcessBusEvent) {
		const text = eventObj.data;

		try {
			clipboard.writeText(text);
			eventObj.respond({
				status: "success",
				message: "Text copied successfully",
			});
		} catch (clipboardWriteError) {
			eventObj.respond({
				status: "error",
				message: `Error writing to system clipboard - ${clipboardWriteError}`,
			});
		}
	}

	/**
	 * Responds to a request to write HTML to clipboard from System
	 */
	writeHTMLToClipboard(eventObj: MainProcessBusEvent) {
		const htmlText = eventObj.data;

		try {
			clipboard.writeHTML(htmlText);
			eventObj.respond({
				status: "success",
				message: "HTML copied successfully",
			});
		} catch (clipboardWriteError) {
			eventObj.respond({
				status: "error",
				message: `Error writing to system clipboard - ${clipboardWriteError}`,
			});
		}
	}

	/**
	 * Responds to a request to write RTF to clipboard from System
	 */
	writeRTFToClipboard(eventObj: MainProcessBusEvent) {
		const rtfText = eventObj.data;

		try {
			clipboard.writeRTF(rtfText);
			eventObj.respond({
				status: "success",
				message: "RTF copied successfully",
			});
		} catch (clipboardWriteError) {
			eventObj.respond({
				status: "error",
				message: `Error writing to system clipboard - ${clipboardWriteError}`,
			});
		}
	}

	/**
	 * Responds to a request to read text from system clipboard
	 */
	readTextFromClipboard(eventObj: MainProcessBusEvent) {
		try {
			const text = clipboard.readText();
			eventObj.respond({ status: "success", value: text });
		} catch (clipboardReadError) {
			eventObj.respond({
				status: "error",
				message: `Error reading from system clipboard - ${clipboardReadError}`,
			});
		}
	}

	/**
	 * Responds to a request to read HTML from system clipboard
	 */
	async readHTMLFromClipboard(eventObj: MainProcessBusEvent) {
		try {
			const html = await clipboard.readHTML();
			eventObj.respond({ status: "success", value: html });
		} catch (clipboardReadError) {
			eventObj.respond({
				status: "error",
				message: `Error reading html from system clipboard - ${clipboardReadError}`,
			});
		}
	}

	/**
	 * Responds to a request to read RTF from system clipboard
	 */
	async readRTFFRomClipboard(eventObj: MainProcessBusEvent) {
		try {
			const rtf = await clipboard.readRTF();
			eventObj.respond({ status: "success", value: rtf });
		} catch (clipboardReadError) {
			eventObj.respond({
				status: "error",
				message: `Error reading RTF from system clipboard - ${clipboardReadError}`,
			});
		}
	}

	async registerGlobalShortcut(eventObj: MainProcessBusEvent) {
		const accelerator = eventObj.data;
		try {
			const ret = globalShortcut.register(accelerator, () => mainBus.sendEvent(`globalShortcut.${accelerator}`, {}));
			eventObj.respond({ status: "success", value: ret });
		} catch (error) {
			eventObj.respond({
				status: "error",
				message: `Error registering ${accelerator} in globalShortcut - ${error}`,
			});
		}
	}

	async isRegisteredGlobalShortcut(eventObj: MainProcessBusEvent) {
		const accelerator = eventObj.data;
		try {
			const ret = globalShortcut.isRegistered(accelerator);
			eventObj.respond({ status: "success", value: ret });
		} catch (error) {
			eventObj.respond({
				status: "error",
				message: `Error checking whether ${accelerator} is in globalShortcut - ${error}`,
			});
		}
	}

	async unregisterGlobalShortcut(eventObj: MainProcessBusEvent) {
		const accelerator = eventObj.data;

		try {
			globalShortcut.unregister(accelerator);
			eventObj.respond({
				status: "success",
				message: `Unregistered ${accelerator} from globalShortcut successfully`,
			});
		} catch (error) {
			eventObj.respond({
				status: "error",
				message: `Error unregistering all from globalShortcut - ${error}`,
			});
		}
	}

	/**
	 * Gets Command Line Arguments
	 */
	getCommandLineArguments(eventObj: MainProcessBusEvent) {
		eventObj.respond(parseArgs(process.argv));
	}

	/**
	 * Responds to checkPermission request from the render process
	 */
	checkPermission(eventObj: MainProcessBusEvent) {
		const { mainWindow } = windowsIndex.byWebContentsId[eventObj.sender.id] ?? {};
		eventObj.respond(permissionsManager.checkPermission(mainWindow, eventObj.data));
	}

	/**
	 * Returns stored protocol URL string. Used in initializeDeepLinking service.
	 */
	getProtocolString(eventObj: MainProcessBusEvent) {
		const { protocolURL } = this.protocolHandler;
		eventObj.respond(this.protocolHandler.isCustom() ? protocolURL : null);
	}

	/**
	 * Sets a flag when the main application is started. This is to handle a case where the splash screen may close before the main application
	 * starts which triggers an event that the last window has closed. We don't want to exit Finsemble if it's still booting up.
	 */
	setMainApplicationStarted() {
		this.mainApplicationStarted = true;
	}
}
